import { useCallback, useRef, useState } from "react";
import { api } from "../../lib/api";
import { customersUrl, useCustomers, type CustomerList, type CustomerListRow } from "../../lib/customers";

/** The Customers list with "Show more" (the home-and-customers round, part three): the first page through the shared fetch layer, the pages
 *  after it appended by their bookmark, kept per list address. A new address (a search, a sort, a segment, a tag, the scope) starts from the
 *  first page; `reload` (after a bulk action, a tag, a licence, the live "customer deleted" note) drops the appended pages and reads page one
 *  again, so the ticks only ever hold rows that are on screen. */
type More = { key: string; rows: CustomerListRow[]; cursor: string | null; loading: boolean };
const NONE: More = { key: "", rows: [], cursor: null, loading: false };

export function useCustomerPages(slug: string, q: Record<string, string | null | undefined> | null) {
  const first = useCustomers(slug, q);
  const key = q ? customersUrl(slug, q) : "";
  const [more, setMore] = useState<More>(NONE);
  const generation = useRef(0); // a page that arrives after a reload belongs to the dropped list
  const extra = more.key === key ? more : null;
  const nextCursor = extra ? extra.cursor : first.data?.nextCursor ?? null;
  const showMore = async () => {
    if (!nextCursor || extra?.loading) return;
    const asked = generation.current;
    setMore((m) => ({ key, rows: m.key === key ? m.rows : [], cursor: nextCursor, loading: true }));
    try {
      const page = await api.get<CustomerList>(`${key}&cursor=${encodeURIComponent(nextCursor)}`);
      if (asked === generation.current) setMore((m) => ({ key, rows: [...(m.key === key ? m.rows : []), ...page.rows], cursor: page.nextCursor, loading: false }));
    } catch { setMore((m) => ({ ...m, loading: false })); }
  };
  const { reload: reloadFirst } = first;
  const reload = useCallback(() => { generation.current += 1; setMore(NONE); return reloadFirst(); }, [reloadFirst]);
  const rows = first.data ? [...first.data.rows, ...(extra?.rows ?? [])] : [];
  return { rows, loaded: !!first.data, loading: first.loading, hasMore: !!nextCursor, busy: !!extra?.loading, showMore, reload };
}
